import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import api from '../api';
import Header from '../components/Header';
import StatsBar from '../components/StatsBar';
import PortalSidePanel from '../components/PortalSidePanel';
import { utcToLocal } from '../utils/time';
import s from './DashboardPage.module.scss';

const BADGE_CLS = {
    free: s => s.bFree, trial: s => s.bTrial, basic: s => s.bBasic,
    extended: s => s.bExt, professional: s => s.bPro,
    main: s => s.bMain, other: s => s.bOther,
};

function PlanBadge({ plan, planLabels }) {
    const cls = (BADGE_CLS[plan] ? BADGE_CLS[plan](s) : null) || s.bOther;
    return <span className={`${s.badge} ${cls}`}>{planLabels[plan] || plan || '—'}</span>;
}

function UsageMini({ used, available }) {
    const pct = available ? Math.min((used / available) * 100, 100) : 0;
    return (
        <div className={s.usageMini}>
            <span className={s.usageNum}>{used}{available != null ? ` / ${available}` : ''}</span>
            {available != null && (
                <div className={s.miniTrack}>
                    <div className={`${s.miniFill} ${pct >= 90 ? s.miniFillWarn : ''}`} style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

// Цикл сортировки: null → asc → desc → null
function nextSort(current, field) {
    if (!current || current.field !== field) return { field, dir: 'asc' };
    if (current.dir === 'asc') return { field, dir: 'desc' };
    return null;
}

function SortIcon({ field, sort }) {
    if (!sort || sort.field !== field) return null;
    return <span className={s.sortActive}>{sort.dir === 'asc' ? '↑' : '↓'}</span>;
}

function SortTh({ field, sort, onSort, children }) {
    return (
        <th className={s.sortable} onClick={() => onSort(field)}>
            <span className={s.thInner}>
                {children}
                <SortIcon field={field} sort={sort} />
            </span>
        </th>
    );
}

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [portals, setPortals] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 100;
    const [search, setSearch] = useState('');
    const [onlyPaying, setOnlyPaying] = useState(false);
    const [onlyInstalled, setOnlyInstalled] = useState(false);
    const [onlyUsesExtension, setOnlyUsesExtension] = useState(false);
    const [sort, setSort] = useState(null); // { field, dir } | null
    const [tableLoading, setTableLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const searchTimer = useRef(null);
    const [plans, setPlans] = useState([]);

    // «Зайти в приложение портала»: бэкенд освежает токен, выдаёт ссылку с JWT
    const [impersonatingId, setImpersonatingId] = useState(null);
    const handleImpersonate = async (portalId) => {
        setImpersonatingId(portalId);
        try {
            const r = await api.post(`/admin/portals/${portalId}/impersonate`);
            window.open(r.data.url, '_blank', 'noopener');
        } catch (e) {
            alert(e?.response?.data?.detail || 'Не удалось создать ссылку для входа');
        } finally {
            setImpersonatingId(null);
        }
    };

    const planLabels = { free: 'Бесплатный', other: 'Другой', ...Object.fromEntries(plans.map(p => [p.id, p.name])) };

    useEffect(() => {
        api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
        api.get('/admin/plans').then(r => setPlans(r.data)).catch(() => {});
    }, []);

    const loadPortals = useCallback(async (p, q, paying, installed, usesExt, sortState) => {
        setTableLoading(true);
        try {
            const params = { page: p, page_size: PAGE_SIZE, only_paying: paying, only_installed: installed, only_uses_extension: usesExt };
            if (q) params.search = q;
            if (sortState) { params.sort_by = sortState.field; params.sort_dir = sortState.dir; }
            const { data } = await api.get('/admin/portals', { params });
            setPortals(data.items);
            setTotal(data.total);
        } finally {
            setTableLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPortals(page, search, onlyPaying, onlyInstalled, onlyUsesExtension, sort);
    }, [page, onlyPaying, onlyInstalled, onlyUsesExtension, sort, loadPortals]);

    const handleSearch = (val) => {
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => { setPage(1); loadPortals(1, val, onlyPaying, onlyInstalled, onlyUsesExtension, sort); }, 350);
    };

    const handleSort = (field) => {
        const next = nextSort(sort, field);
        setSort(next);
        setPage(1);
    };

    const handleFilter = (key, val) => {
        if (key === 'paying') { setOnlyPaying(val); setPage(1); }
        if (key === 'installed') { setOnlyInstalled(val); setPage(1); }
        if (key === 'uses_extension') { setOnlyUsesExtension(val); setPage(1); }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const refresh = () => {
        loadPortals(page, search, onlyPaying, onlyInstalled, onlyUsesExtension, sort);
        api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    };

    return (
        <div className={s.root}>
            <Header />
            <div className={s.workspace}>
                <div className={s.content}>
                    <div className={s.inner}>
                        <div className={s.titleRow}>
                            <h1 className={s.title}>Порталы</h1>
                            <span className={s.count}>{total}</span>
                        </div>

                        <StatsBar stats={stats} />

                        <div className={s.toolbar}>
                            <div className={s.searchBox}>
                                <span className={s.searchIcon}>⌕</span>
                                <input
                                    className={s.searchInput}
                                    placeholder="Поиск по домену..."
                                    value={search}
                                    onChange={e => handleSearch(e.target.value)}
                                />
                                {search && <button className={s.clearBtn} onClick={() => handleSearch('')}>✕</button>}
                            </div>
                            <label className={s.checkLabel}>
                                <input type="checkbox" checked={onlyInstalled} onChange={e => handleFilter('installed', e.target.checked)} />
                                Только установленные
                            </label>
                            <label className={s.checkLabel}>
                                <input type="checkbox" checked={onlyUsesExtension} onChange={e => handleFilter('uses_extension', e.target.checked)} />
                                Используют расширение
                            </label>
                            <label className={s.checkLabel}>
                                <input type="checkbox" checked={onlyPaying} onChange={e => handleFilter('paying', e.target.checked)} />
                                Только платящие
                            </label>
                        </div>

                        <div className={s.tableWrap}>
                            {tableLoading && <div className={s.tableLoader}><div className={s.spinner} /></div>}
                            <table className={s.table}>
                                <colgroup>
                                    <col style={{ width: '52px' }} />
                                    <col style={{ width: '220px' }} />
                                    <col style={{ width: '105px' }} />
                                    <col style={{ width: '135px' }} />
                                    <col style={{ width: '110px' }} />
                                    <col style={{ width: '155px' }} />
                                    <col style={{ width: '175px' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '110px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className={s.rowNumTh}>#</th>
                                        <SortTh field="host" sort={sort} onSort={handleSort}>Хост</SortTh>
                                        <SortTh field="registered_at" sort={sort} onSort={handleSort}>Регистрация</SortTh>
                                        <SortTh field="plan_type" sort={sort} onSort={handleSort}>Тариф</SortTh>
                                        <SortTh field="subscription_end_date" sort={sort} onSort={handleSort}>Истекает</SortTh>
                                        <th>AI-запросы (30 дн)</th>
                                        <SortTh field="extension_users" sort={sort} onSort={handleSort}>Пользователи расширения</SortTh>
                                        <th style={{ textAlign: 'center' }}>Установлено</th>
                                        <th style={{ textAlign: 'center' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {portals.length === 0 && !tableLoading ? (
                                        <tr><td colSpan={9} className={s.empty}>{search ? 'Ничего не найдено' : 'Нет данных'}</td></tr>
                                    ) : portals.map((p, i) => (
                                        <PortalRow key={p.id} portal={p} index={(page - 1) * PAGE_SIZE + i + 1} selected={selectedId === p.id} onSelect={() => setSelectedId(p.id)} onImpersonate={() => handleImpersonate(p.id)} impersonating={impersonatingId === p.id} planLabels={planLabels} />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className={s.pager}>
                                <button className={s.pageBtn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Назад</button>
                                <span className={s.pageNum}>Страница {page} из {totalPages}</span>
                                <button className={s.pageBtn} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Вперёд →</button>
                            </div>
                        )}

                    </div>
                </div>

                <div className={`${s.panel} ${selectedId ? s.panelOpen : ''}`}>
                    {selectedId && <PortalSidePanel portalId={selectedId} onClose={() => setSelectedId(null)} onSaved={refresh} plans={plans} />}
                </div>
            </div>
        </div>
    );
}

function PortalRow({ portal, index, selected, onSelect, onImpersonate, impersonating, planLabels }) {
    const end = portal.subscription_end_date;
    const daysLeft = end ? utcToLocal(end).diff(moment(), 'days') : null;
    const expired = daysLeft !== null && daysLeft < 0;
    const soon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;

    return (
        <tr className={`${s.row} ${selected ? s.rowSelected : ''}`}>
            <td className={s.rowNum}>{index}</td>
            <td>
                <div className={s.host}>{portal.domain}</div>
                {portal.inn && <div className={s.inn}>ИНН {portal.inn}</div>}
            </td>
            <td className={s.date}>{utcToLocal(portal.registered_at).format('DD.MM.YYYY')}</td>
            <td>{portal.plan_type ? <PlanBadge plan={portal.plan_type} planLabels={planLabels} /> : <span className={s.dash}>—</span>}</td>
            <td className={s.date}>
                {end ? (
                    <span className={expired ? s.expired : soon ? s.soon : ''}>
                        {utcToLocal(end).format('DD.MM.YYYY')}
                        {soon && !expired && <span className={s.dayHint}> ({daysLeft}д)</span>}
                        {expired && <span className={s.dayHint}> (истекла)</span>}
                    </span>
                ) : '—'}
            </td>
            <td><UsageMini used={portal.ai_requests.used} available={portal.ai_requests.available} /></td>
            <td><UsageMini used={portal.extension_users.used} available={portal.extension_users.available} /></td>
            <td className={s.dotCell}><span className={portal.is_installed ? s.dotGreen : s.dotRed} /></td>
            <td className={s.editCell}>
                <div className={s.rowActions}>
                    <button className={s.editBtn} onClick={onSelect}>Изменить</button>
                    <button
                        className={s.enterBtn}
                        title="Зайти в приложение портала"
                        disabled={impersonating}
                        onClick={(e) => { e.stopPropagation(); onImpersonate(); }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                            <polyline points="10 17 15 12 10 7"/>
                            <line x1="15" y1="12" x2="3" y2="12"/>
                        </svg>
                    </button>
                </div>
            </td>
        </tr>
    );
}
